import React, { useEffect, useMemo, useState } from "react";
import {
  Edit as EditIcon,
  Trash2 as TrashIcon,
  RotateCcw as RestoreIcon,
  KeyRound,
  LogIn,
  Search,
  Filter,
  X,
  Download,
  Upload,
  Plus,
} from "lucide-react";
import {
  DirectionsCarFilled as FleetVehicleIcon,
  PersonAddAlt as FleetUserAssignIcon,
  WorkOutline as ManagerRoleIcon,
  AssignmentInd as HrRoleIcon,
  Badge as OfficeRoleIcon,
  Engineering as FieldRoleIcon,
  AdminPanelSettings as AdminRoleIcon,
  Apartment as DealerRoleIcon,
  PersonOutline as BasicUserRoleIcon,
  VisibilityOutlined as ViewerRoleIcon,
  Security as PermissionIcon,
} from "@mui/icons-material";

import { ROLES, getUserActionsEffective } from "../../utils/rbacHelper";

// ✅ logic imports
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useImpersonateUserMutation } from "../../redux/admin/adminApi";
import { loginSuccess } from "../../store/authSlice";
import { adminApi } from "../../redux/admin/adminApi";

// ✅ MUI
import {
  Box,
  Stack,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Switch,
  FormControlLabel,
  FormGroup,
  Grid,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Tabs,
  Tab,
  Badge,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { downloadEntityTemplate, uploadEntityFile } from "../../services/importExportService";
import ScrollableTableContainer from "../common/ScrollableTableContainer";
import CreateUserModal from "./CreateUserModal";
import AddDealerModal from "../modals/AddDealerModal";
import AddSubAdminModal from "../modals/AddSubAdminModal";
import AddUserModal from "../modals/AddUserModal";
import ViewerPortalModal from "../modals/DriverPortalModal";
import { useEditUserMutation } from "../../redux/admin/adminApi";

/** =========================
 * HIDE KEYS (as per your latest ask)
 * ========================= */
const HIDE_KEYS = new Set([
  // ids + createdBy
  "_id",
  "id",
  "createdBy",
  "createdBY",
  "created_by",
  "createdby",

  // security / mongo
  "password",
  "__v",

  // asked to remove
  "notificationPreferences",
  "notification_preferences",
  "preferences",
  "prefrences",
  "preference",
  "fcmTokens",
  "fcmToken",
  "fcm_tokens",
  "fcm_token",
  "subAdminOwner",
  "subOwner",
  "subOwners",
  "subowners",
  "dealerOwner",
  "profilePicture",
  "userPermissions",
  "devicePermissions",
  "licenseSummary",
  "remainingTokens",
  "demoLocked",
  "demoLockReason",
]);

/** Mongo extended JSON unwrapping */
const unwrapMongoValue = (v) => {
  if (v && typeof v === "object" && !Array.isArray(v)) {
    if ("$oid" in v) return v.$oid;
    if ("$date" in v) return v.$date;
  }
  return v;
};

const isProbablyDate = (v, key = "") => {
  const val = unwrapMongoValue(v);
  if (!val) return false;

  const k = String(key).toLowerCase();
  if (k.includes("date") || k.includes("time") || k.endsWith("at")) return true;

  if (typeof val === "string") {
    const t = Date.parse(val);
    return Number.isFinite(t) && val.length >= 8;
  }
  if (val instanceof Date) return true;
  return false;
};

const formatValue = (v, key) => {
  const val = unwrapMongoValue(v);

  if (val === null || val === undefined) return "-";
  if (typeof val === "boolean") return val ? "Yes" : "No";

  if (isProbablyDate(val, key)) {
    const d = val instanceof Date ? val : new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleString();
  }

  if (Array.isArray(val)) {
    if (val.length === 0) return "-";
    if (typeof val[0] === "object") {
      return (
        val
          .map(
            (x) =>
              x?.username ||
              x?.name ||
              x?.email ||
              unwrapMongoValue(x?._id) ||
              ""
          )
          .filter(Boolean)
          .join(", ") || `${val.length} items`
      );
    }
    return val.join(", ");
  }

  if (typeof val === "object") {
    try {
      return JSON.stringify(val);
    } catch {
      return "[Object]";
    }
  }

  return String(val);
};

const permissionLabelFromKey = (key) =>
  String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const collectPermissionLabels = (permissions = {}) => {
  const labels = [];

  Object.entries(permissions || {}).forEach(([groupKey, groupValue]) => {
    if (groupKey === "limits" || !groupValue || typeof groupValue !== "object") return;

    Object.entries(groupValue).forEach(([itemKey, itemValue]) => {
      if (itemValue === true) {
        labels.push(`${permissionLabelFromKey(groupKey)}: ${permissionLabelFromKey(itemKey)}`);
      }
    });
  });

  const deviceLimit = Number(permissions?.limits?.deviceLimit || 0);
  const userLimit = Number(permissions?.limits?.userLimit || 0);

  if (deviceLimit > 0) labels.push(`Device Limit: ${deviceLimit}`);
  if (userLimit > 0) labels.push(`User Limit: ${userLimit}`);

  return labels;
};

const collectDeletedIdentityLabels = (deletedIdentity = {}) => {
  const fields = [
    ["username", "Username"],
    ["email", "Email"],
    ["mobileNo", "Mobile"],
    ["uniqueId", "User ID"],
  ];

  return fields
    .map(([key, label]) => {
      const value = deletedIdentity?.[key];
      return value ? `${label}: ${value}` : null;
    })
    .filter(Boolean);
};

const headerLabelFromKey = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getInitials = (name) => {
  if (!name) return "U";
  return name
    .split(" ")
    .map((s) => s[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const IDENTITY_COLUMNS = ["profile", "name", "uniqueId", "email"];
const STICKY_LEFT_MAP = {
  profile: 0,
  name: 92,
  uniqueId: 292,
  email: 472,
};

const roleLabel = (role) => {
  const normalized = normalizeRole(role);
  return CREATE_ROLE_CATALOG[normalized]?.title || (normalized === ROLES.DEALER ? "Dealer" : normalized);
};

const CREATE_ROLE_CATALOG = {
  [ROLES.SUB_ADMIN]: {
    title: "Admin",
    description: "Admin users who manage operational access and user controls.",
    icon: AdminRoleIcon,
    tone: "#2563eb",
  },
  [ROLES.DEALER]: {
    title: "Dealer",
    description: "Dealer accounts with customer and license distribution access.",
    icon: DealerRoleIcon,
    tone: "#0f766e",
  },
  [ROLES.USER]: {
    title: "User",
    description: "Standard users for day-to-day tracking access.",
    icon: BasicUserRoleIcon,
    tone: "#475569",
  },
  [ROLES.VIEWER]: {
    title: "Viewer",
    description: "View-only users for shared tracking and monitoring access.",
    icon: ViewerRoleIcon,
    tone: "#0f766e",
  },
  [ROLES.MANAGER]: {
    title: "Manager",
    description: "Full management access for operations and teams.",
    icon: ManagerRoleIcon,
    tone: "#2563eb",
  },
  [ROLES.HR]: {
    title: "HR",
    description: "People operations and employee coordination access.",
    icon: HrRoleIcon,
    tone: "#db2777",
  },
  [ROLES.OFFICE_EMPLOYEE]: {
    title: "Office Employee",
    description: "Back-office workflow and coordination access.",
    icon: OfficeRoleIcon,
    tone: "#7c3aed",
  },
  [ROLES.FIELD_EMPLOYEE]: {
    title: "Field Employee",
    description: "Ground operations and field execution access.",
    icon: FieldRoleIcon,
    tone: "#16a34a",
  },
};

const normalizeRole = (role) => {
  const r = String(role || "").trim().toLowerCase();
  if (r === "superadmin" || r === "super_admin") return ROLES.SUPER_ADMIN;
  if (r === "groupadmin" || r === "dealer") return ROLES.DEALER;
  if (r === "subadmin") return ROLES.SUB_ADMIN;
  if (r === "officeemployee") return ROLES.OFFICE_EMPLOYEE;
  if (r === "fieldemployee") return ROLES.FIELD_EMPLOYEE;
  return r;
};

/** ✅ Remaining tokens: prefer license summary, fallback to legacy credits if needed */
const CreditsCell = ({ credits, licenseSummary, remainingTokens }) => {
  const c = unwrapMongoValue(credits) || {};
  const summary = unwrapMongoValue(licenseSummary) || {};
  const balance = Number(
    remainingTokens ??
      summary.totalRemainingCredits ??
      c.balance ??
      0
  );

  return (
    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
      <Chip size="small" label={`Remaining Tokens: ${balance}`} sx={{ fontWeight: 900, borderRadius: 2 }} />
    </Stack>
  );
};

/** =========================
 * MAIN USER MANAGEMENT
 * ========================= */
function UserManagement({
  users,
  creatableRoles,
  manageableRoles,
  currentUser,
  currentUserId,
  currentUserRole,
  setEditingUser,
  handleDeleteUser,
  handleRestoreUser,
  handleRemoveManagedUser,
  setResetUserId,
  setShowResetPasswordModal,
  setShowCreateUserModal,
  canAssignDevice,
  canAssignUserToGA,
  assignmentLabels,
  handleAssignDevice,
  handleAssignUserToGroupAdmin,
  safeDevices = [],
  assignableUsersForDevices = [],
  assignableUsersForGroupAdmin = [],
  groupAdmins = [],
  deviceSearchQuery,
  setDeviceSearchQuery,
  assignDeviceId,
  setAssignDeviceId,
  userSearchQuery,
  setUserSearchQuery,
  assignToUserId,
  setAssignToUserId,
  assignUserSearchQuery,
  setAssignUserSearchQuery,
  assignUserToGroupAdminUserId,
  setAssignUserToGroupAdminUserId,
  groupAdminSearchQuery,
  setGroupAdminSearchQuery,
  assignUserToGroupAdminTargetId,
  setAssignUserToGroupAdminTargetId,
}) {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const actionButtonSx = (tone = "default") => {
    const tones = {
      default: {
        color: theme.palette.text.secondary,
        bg: alpha(theme.palette.text.primary, 0.04),
        hoverBg: alpha(theme.palette.text.primary, 0.1),
      },
      primary: {
        color: theme.palette.primary.main,
        bg: alpha(theme.palette.primary.main, 0.1),
        hoverBg: alpha(theme.palette.primary.main, 0.18),
      },
      success: {
        color: theme.palette.success.main,
        bg: alpha(theme.palette.success.main, 0.12),
        hoverBg: alpha(theme.palette.success.main, 0.2),
      },
      warning: {
        color: theme.palette.warning.dark,
        bg: alpha(theme.palette.warning.main, 0.14),
        hoverBg: alpha(theme.palette.warning.main, 0.22),
      },
      info: {
        color: theme.palette.info.main,
        bg: alpha(theme.palette.info.main, 0.12),
        hoverBg: alpha(theme.palette.info.main, 0.2),
      },
      error: {
        color: theme.palette.error.main,
        bg: alpha(theme.palette.error.main, 0.12),
        hoverBg: alpha(theme.palette.error.main, 0.2),
      },
    };

    const palette = tones[tone] || tones.default;
    return {
      color: palette.color,
      backgroundColor: palette.bg,
      border: `1px solid ${alpha(palette.color, 0.14)}`,
      transition: "all 0.18s ease",
      "&:hover": {
        backgroundColor: palette.hoverBg,
        transform: "translateY(-1px)",
      },
    };
  };

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isDeletedFilter, setIsDeletedFilter] = useState("false");

  // ✅ Role tabs state
  const [roleTab, setRoleTab] = useState("all");

  // Dashboard modal
  const [assignDeviceModalOpen, setAssignDeviceModalOpen] = useState(false);
  const [assignGroupModalOpen, setAssignGroupModalOpen] = useState(false);
  const [usersUploading, setUsersUploading] = useState(false);
  const userUploadInputRef = React.useRef(null);

  // Sorting
  const [orderBy, setOrderBy] = useState("updatedAt");
  const [order, setOrder] = useState("desc");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const userActions = getUserActionsEffective(currentUser);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [impersonateUser] = useImpersonateUserMutation();
  const [editUser] = useEditUserMutation();
  const canShowCreateUserButton = useMemo(
    () => userActions.canCreate && Array.isArray(creatableRoles) && creatableRoles.length > 0,
    [creatableRoles, userActions.canCreate]
  );

  const openAssignDeviceModal = (user) => {
    setAssignToUserId?.(user?._id || "");
    setUserSearchQuery?.(
      user ? `${user.username || user.name || "User"} (${user.mobileNo || user.email || "-"})` : ""
    );
    setAssignDeviceModalOpen(true);
  };

  const openAssignGroupModal = (user) => {
    setAssignUserToGroupAdminUserId?.(user?._id || "");
    setAssignUserSearchQuery?.(user?.username || user?.name || "");
    setAssignGroupModalOpen(true);
  };

  // keep pagination reset
  useEffect(() => setPage(0), [searchQuery, roleFilter, statusFilter, isDeletedFilter, roleTab]);

  // sync roleFilter with roleTab (tabs control main)
  useEffect(() => {
    setRoleFilter(roleTab);
  }, [roleTab]);

const handleLoginAsUser = async (targetUserId) => {
  if (!userActions.canLoginAsUser) {
    alert("You don't have permission to login as other users.");
    return;
  }

  if (
    !window.confirm(
      "Are you sure you want to log in as this user? You will be logged out of Admin."
    )
  ) {
    return;
  }

  try {
    const currentAdminToken = localStorage.getItem("token");
    if (currentAdminToken) localStorage.setItem("adminFallbackToken", currentAdminToken);

    const response = await impersonateUser(targetUserId).unwrap();

    // ✅ Clear RTK Query cache
    dispatch(adminApi.util.resetApiState());

    // ✅ (Optional) Clear whole redux app state if you implemented app/reset
    // dispatch({ type: "app/reset" });

    // ✅ Set new auth
    dispatch(loginSuccess({ user: response.user, token: response.token }));

    // ✅ IMPORTANT: go to USER portal route (change /dashboard to your user portal path)
    navigate("/dashboard", { replace: true, state: { __impersonate: true, t: Date.now() } });
  } catch (error) {
    alert(error?.data?.msg || "Failed to login as user.");
  }
};

  const onDownloadUsersTemplate = async () => {
    try {
      await downloadEntityTemplate("users");
    } catch (error) {
      alert(error?.response?.data?.message || "Users template download failed.");
    }
  };

  const onUsersFileSelected = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    setUsersUploading(true);
    try {
      const result = await uploadEntityFile("users", file, { validateOnly: false });
      alert(result?.message || "Users upload completed.");
    } catch (error) {
      const payload = error?.response?.data;
      const details = Array.isArray(payload?.errors) ? payload.errors.slice(0, 5).join("\n") : "";
      alert(`${payload?.message || "Users upload failed."}${details ? `\n${details}` : ""}`);
    } finally {
      if (userUploadInputRef.current) userUploadInputRef.current.value = "";
      setUsersUploading(false);
    }
  };

  const handleToggleDisabled = async (user, disabled) => {
    if (!userActions.canEdit) return;
    if (user?._id === currentUserId && currentUserRole === ROLES.SUPER_ADMIN) return;

    try {
      await editUser({
        userId: user._id,
        isActive: !disabled,
      }).unwrap();
    } catch (error) {
      alert(error?.data?.msg || "Failed to update account status.");
    }
  };



  // ✅ filtering
  const filteredUsers = useMemo(() => {
    return (users || [])
      .filter((user) => normalizeRole(user.role) !== ROLES.SUPER_ADMIN)
      .filter((user) => {
        const query = searchQuery.toLowerCase();
        return (
          user.username?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          String(user.mobileNo || "").includes(query)
        );
      })
      .filter((user) => roleFilter === "all" || normalizeRole(user.role) === roleFilter)
      .filter((user) => {
        if (statusFilter === "all") return true;
        return statusFilter === "active" ? user.isActive : !user.isActive;
      })
      .filter((user) => {
        if (isDeletedFilter === "all") return true;
        return String(user.isDeleted) === isDeletedFilter;
      })
      .filter((user) => {
        const highLevelStaff = [
          ROLES.SUPER_ADMIN,
          ROLES.MANAGER,
          ROLES.OFFICE_EMPLOYEE,
          ROLES.HR,
          ROLES.SUB_ADMIN,
        ];

        if (highLevelStaff.includes(currentUserRole)) return true;

        if (currentUserRole === ROLES.DEALER) {
          return (
            user._id === currentUserId ||
            user.dealerOwner === currentUserId ||
            user.createdBy === currentUserId ||
            currentUser?.managedUsers?.some((mu) => mu._id === user._id)
          );
        }

        return (
          user._id === currentUserId ||
          user.createdBy === currentUserId ||
          currentUser?.managedUsers?.some((mu) => mu._id === user._id)
        );
      });
  }, [
    users,
    searchQuery,
    roleFilter,
    statusFilter,
    isDeletedFilter,
    currentUserRole,
    currentUserId,
    currentUser,
  ]);

  // ✅ counts per role for badges (based on current "search/status/deleted" filters except role)
  const roleCounts = useMemo(() => {
    const base = (users || [])
      .filter((u) => u.role !== ROLES.SUPER_ADMIN)
      .filter((u) => {
        const query = searchQuery.toLowerCase();
        return (
          u.username?.toLowerCase().includes(query) ||
          u.email?.toLowerCase().includes(query) ||
          String(u.mobileNo || "").includes(query)
        );
      })
      .filter((u) => {
        if (statusFilter === "all") return true;
        return statusFilter === "active" ? u.isActive : !u.isActive;
      })
      .filter((u) => {
        if (isDeletedFilter === "all") return true;
        return String(u.isDeleted) === isDeletedFilter;
      })
      .filter((u) => {
        const highLevelStaff = [
          ROLES.SUPER_ADMIN,
          ROLES.MANAGER,
          ROLES.OFFICE_EMPLOYEE,
          ROLES.HR,
          ROLES.SUB_ADMIN,
        ];

        if (highLevelStaff.includes(currentUserRole)) return true;

        if (currentUserRole === ROLES.DEALER) {
          return (
            u._id === currentUserId ||
            u.dealerOwner === currentUserId ||
            u.createdBy === currentUserId ||
            currentUser?.managedUsers?.some((mu) => mu._id === u._id)
          );
        }

        return (
          u._id === currentUserId ||
          u.createdBy === currentUserId ||
          currentUser?.managedUsers?.some((mu) => mu._id === u._id)
        );
      });

    const counts = {};
    counts.all = base.length;
    (manageableRoles || []).forEach((r) => {
      counts[r] = base.filter((u) => normalizeRole(u.role) === r).length;
    });
    return counts;
  }, [
    users,
    manageableRoles,
    searchQuery,
    statusFilter,
    isDeletedFilter,
    currentUserRole,
    currentUserId,
    currentUser,
  ]);

  // ✅ Dynamic columns (with removals)
  const dynamicColumns = useMemo(() => {
    const sample = filteredUsers[0] || users?.[0];
    const keys = sample ? Object.keys(sample) : [];

    const preferred = [
      "role",
      "isActive",
      "isDeleted",
      "mobileNo",
      "credits",
      "updatedAt",
      "createdAt",
    ];

    const visible = keys.filter(
      (k) => !HIDE_KEYS.has(k) && k !== "settings" && !["username", "email", "uniqueId"].includes(k)
    );

    const first = preferred.filter((k) => visible.includes(k));
    const rest = visible.filter((k) => !first.includes(k));

    return [...IDENTITY_COLUMNS, ...first, ...rest];
  }, [filteredUsers, users]);

  // Sorting
  const sortedUsers = useMemo(() => {
    const arr = [...filteredUsers];

    const getVal = (u, key) => {
      const raw = unwrapMongoValue(u?.[key]);

      if (isProbablyDate(raw, key)) {
        const t = new Date(raw).getTime();
        return Number.isFinite(t) ? t : 0;
      }
      if (typeof raw === "boolean") return raw ? 1 : 0;
      if (typeof raw === "number") return raw;
      if (typeof raw === "string") return raw.toLowerCase();
      if (Array.isArray(raw)) return raw.length;
      if (raw && typeof raw === "object") return JSON.stringify(raw);
      return raw ?? "";
    };

    arr.sort((a, b) => {
      const av = getVal(a, orderBy);
      const bv = getVal(b, orderBy);
      if (av < bv) return order === "asc" ? -1 : 1;
      if (av > bv) return order === "asc" ? 1 : -1;
      return 0;
    });

    return arr;
  }, [filteredUsers, orderBy, order]);

  const onRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // ✅ cell renderer
  const renderCell = (user, key) => {
    if (key === "profile") {
      return (
        <Stack direction="row" justifyContent="center" sx={{ minWidth: 60 }}>
          <Avatar sx={{ width: 36, height: 36, fontWeight: 900 }}>
            {getInitials(user.username)}
          </Avatar>
        </Stack>
      );
    }

    if (key === "name") {
      return (
        <Typography sx={{ fontWeight: 900, minWidth: 160 }} noWrap>
          {user.username || user.name || "-"}
        </Typography>
      );
    }

    if (key === "email") {
      return (
        <Typography variant="body2" sx={{ minWidth: 150 }} noWrap>
          {user.email || "-"}
        </Typography>
      );
    }

    if (key === "uniqueId") {
      return (
        <Typography variant="body2" sx={{ fontWeight: 800, minWidth: 130 }} noWrap>
          {user.uniqueId || "-"}
        </Typography>
      );
    }

    if (key === "role") {
      return (
        <Chip
          size="small"
          label={roleLabel(user.role)}
          sx={{
            fontWeight: 900,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
          }}
        />
      );
    }

    if (key === "isActive") {
      const isDisabled = user.isActive === false;
      const canToggle =
        userActions.canEdit &&
        !(user._id === currentUserId && currentUserRole === ROLES.SUPER_ADMIN) &&
        !user.isDeleted;

      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Switch
            checked={isDisabled}
            disabled={!canToggle}
            onChange={(event) => handleToggleDisabled(user, event.target.checked)}
            color="warning"
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: isDisabled ? theme.palette.warning.dark : theme.palette.success.dark,
            }}
          >
            {isDisabled ? "Disabled" : "Enabled"}
          </Typography>
        </Stack>
      );
    }

    if (key === "isDeleted") {
      return user.isDeleted ? (
        <Chip
          size="small"
          label="Deleted"
          sx={{
            fontWeight: 900,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.14),
            color: theme.palette.error.main,
          }}
        />
      ) : (
        <Typography color="text.secondary">-</Typography>
      );
    }

    if (key === "credits") {
      return (
        <CreditsCell
          credits={user.credits}
          licenseSummary={user.licenseSummary}
          remainingTokens={user.remainingTokens}
        />
      );
    }

    if (key === "permissions") {
      const labels = collectPermissionLabels(user.permissions);
      if (!labels.length) return <Typography color="text.secondary">-</Typography>;

      return (
        <Box
          sx={{
            display: "flex",
            gap: 0.8,
            overflowX: "auto",
            overflowY: "hidden",
            whiteSpace: "nowrap",
            pb: 0.5,
            maxWidth: { xs: 260, md: 420 },
            "&::-webkit-scrollbar": {
              height: 6,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(theme.palette.text.primary, 0.18),
              borderRadius: 999,
            },
          }}
        >
          {labels.map((label) => (
            <Chip
              key={label}
              size="small"
              label={label}
              variant="outlined"
              sx={{ borderRadius: 2, fontWeight: 700, flexShrink: 0 }}
            />
          ))}
        </Box>
      );
    }

    if (key === "deletedIdentity") {
      const labels = collectDeletedIdentityLabels(user.deletedIdentity);
      if (!labels.length) return <Typography color="text.secondary">-</Typography>;

      return (
        <Stack spacing={0.4}>
          {labels.map((label) => (
            <Typography key={label} variant="body2" sx={{ whiteSpace: "nowrap" }}>
              {label}
            </Typography>
          ))}
        </Stack>
      );
    }

    if (key === "managedUsers") {
      const list = user.managedUsers || [];
      if (!Array.isArray(list) || list.length === 0)
        return <Typography color="text.secondary">-</Typography>;

      const canShow = ["group_admin", "manager", "hr", "sub_admin"].includes(user.role);
      if (!canShow) return <Typography color="text.secondary">-</Typography>;

      return (
        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
          {list.map((mu) => (
            <Chip
              key={unwrapMongoValue(mu._id) || mu._id}
              label={mu.username || mu.email || "User"}
              size="small"
              variant="outlined"
              onDelete={
                userActions.canRemoveManaged
                  ? (e) => {
                      e.stopPropagation();
                      handleRemoveManagedUser(user._id, mu._id);
                    }
                  : undefined
              }
              sx={{ borderRadius: 2, fontWeight: 800 }}
            />
          ))}
        </Stack>
      );
    }

    return (
      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
        {formatValue(user[key], key)}
      </Typography>
    );
  };

  const headerLabel = (key) => {
    const map = {
      profile: "User Profile",
      name: "Name",
      uniqueId: "User ID",
      email: "Email",
      mobileNo: "Contact",
      isActive: "Disabled",
      isDeleted: "Deleted",
      deletedIdentity: "Deleted Identity",
      permissions: "Permissions",
      updatedAt: "Last Updated",
      createdAt: "Created",
      credits: "Remaining Tokens",
    };
    if (map[key]) return map[key];
    return headerLabelFromKey(key);
  };

  const roleTabs = useMemo(() => {
    const list = ["all", ...(manageableRoles || [])];
    // avoid duplicates
    return Array.from(new Set(list));
  }, [manageableRoles]);

  return (
    <Box
      sx={{
        p: { xs: 1.25, md: 2.5 },
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, md: 2 },
          borderRadius: 4,
          borderColor: alpha("#000", 0.12),
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 20, md: 24 } }}>
              User Management
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Manage access, roles, and permissions securely.
            </Typography>
          </Box>

          {userActions.canCreate && canShowCreateUserButton && setShowCreateUserModal && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", md: "auto" } }}>
              <Button
                variant="outlined"
                startIcon={<Download size={16} />}
                onClick={onDownloadUsersTemplate}
                sx={{ borderRadius: 2.5, fontWeight: 900, px: 2.2, py: 1.1 }}
              >
                Users Template
              </Button>
              <Button
                variant="outlined"
                startIcon={<Upload size={16} />}
                disabled={usersUploading}
                onClick={() => userUploadInputRef.current?.click()}
                sx={{ borderRadius: 2.5, fontWeight: 900, px: 2.2, py: 1.1 }}
              >
                {usersUploading ? "Uploading..." : "Upload Users"}
              </Button>
              <Button
                variant="contained"
                onClick={() => setShowCreateUserModal?.(true)}
                sx={{ borderRadius: 2.5, fontWeight: 900, px: 2.2, py: 1.1, width: { xs: "100%", md: "auto" } }}
              >
                + Create New User
              </Button>
              <input
                ref={userUploadInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: "none" }}
                onChange={onUsersFileSelected}
              />
            </Stack>
          )}
        </Stack>

        {/* ✅ Role Tabs (separate roles) */}
        <Box sx={{ mt: 2 }}>
          {isSmDown ? (
            <FormControl size="small" fullWidth>
              <Select
                value={roleTab}
                onChange={(e) => setRoleTab(e.target.value)}
                startAdornment={
                  <InputAdornment position="start" sx={{ ml: 0.5 }}>
                    <Filter size={16} />
                  </InputAdornment>
                }
              >
                {roleTabs.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r === "all" ? "All Roles" : roleLabel(r)}
                    {typeof roleCounts?.[r] === "number" ? ` (${roleCounts[r]})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Tabs
              value={roleTab}
              onChange={(_, v) => setRoleTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 42,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 900,
                  minHeight: 42,
                  px: 2,
                  borderRadius: 2,
                },
                "& .MuiTabs-flexContainer": { gap: 1 },
              }}
            >
              {roleTabs.map((r) => (
                <Tab
                  key={r}
                  value={r}
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography sx={{ fontWeight: 900 }}>
                        {r === "all" ? "All Roles" : roleLabel(r)}
                      </Typography>
                      <Badge
                        badgeContent={roleCounts?.[r] ?? 0}
                        color="primary"
                        max={999}
                        sx={{
                          "& .MuiBadge-badge": {
                            fontWeight: 900,
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Stack>
                  }
                  sx={{
                    bgcolor: roleTab === r ? alpha(theme.palette.primary.main, 0.12) : "transparent",
                    border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  }}
                />
              ))}
            </Tabs>
          )}
        </Box>
      </Paper>

      {/* Controls */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, md: 2 },
          borderRadius: 4,
          borderColor: alpha("#000", 0.12),
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search users by name / email / mobile..."
            fullWidth
            sx={{ flex: 1, minWidth: { md: 360 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
            }}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.2}
            sx={{
              width: { xs: "100%", md: "auto" },
              flexWrap: "wrap",
              justifyContent: { xs: "stretch", md: "flex-end" },
              alignItems: "stretch",
            }}
          >
            {/* ✅ role dropdown removed from desktop (tabs already). Keep only on mdDown as optional quick switch. */}
            {isMdDown && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={roleTab}
                  onChange={(e) => setRoleTab(e.target.value)}
                  displayEmpty
                  startAdornment={
                    <InputAdornment position="start" sx={{ ml: 0.5 }}>
                      <Filter size={16} />
                    </InputAdornment>
                  }
                >
                  {roleTabs.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role === "all" ? "All Roles" : roleLabel(role)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active Only</MenuItem>
                <MenuItem value="disabled">Disabled Only</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select value={isDeletedFilter} onChange={(e) => setIsDeletedFilter(e.target.value)}>
                <MenuItem value="all">All Records</MenuItem>
                <MenuItem value="false">Active Users</MenuItem>
                <MenuItem value="true">Deleted Users</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          borderColor: alpha("#000", 0.12),
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 280,
        }}
      >
        {sortedUsers.length > 0 ? (
          <>
            <ScrollableTableContainer
              sx={{
                flex: 1,
                maxHeight: { xs: "calc(100vh - 360px)", md: "calc(100vh - 330px)" },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {dynamicColumns.map((key) => (
                      <TableCell
                        key={key}
                        sx={{
                          fontWeight: 1000,
                          bgcolor: alpha(theme.palette.background.paper, 0.98),
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                          whiteSpace: "nowrap",
                          minWidth:
                            key === "profile" ? 92 :
                            key === "name" ? 200 :
                            key === "uniqueId" ? 180 :
                            key === "email" ? 220 : undefined,
                          position: IDENTITY_COLUMNS.includes(key) ? "sticky" : "static",
                          left: IDENTITY_COLUMNS.includes(key) ? `${STICKY_LEFT_MAP[key]}px` : undefined,
                          zIndex: IDENTITY_COLUMNS.includes(key) ? 4 : 2,
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === key}
                          direction={orderBy === key ? order : "asc"}
                          onClick={() => onRequestSort(key)}
                        >
                          {headerLabel(key)}
                        </TableSortLabel>
                      </TableCell>
                    ))}

                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 1000,
                          bgcolor: alpha(theme.palette.background.paper, 0.98),
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                        whiteSpace: "nowrap",
                        minWidth: 170,
                        position: "sticky",
                        right: 0,
                        zIndex: 4,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sortedUsers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((user) => (
                      <TableRow
                        key={unwrapMongoValue(user._id) || user._id}
                        hover
                        sx={{
                          bgcolor: user.isDeleted
                            ? alpha(theme.palette.error.main, 0.05)
                            : "transparent",
                        }}
                      >
                        {dynamicColumns.map((key) => (
                          <TableCell
                            key={key}
                            sx={{
                              whiteSpace: "nowrap",
                              maxWidth: { xs: 260, md: 420 },
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              bgcolor: IDENTITY_COLUMNS.includes(key)
                                ? alpha(theme.palette.background.paper, 0.98)
                                : undefined,
                              position: IDENTITY_COLUMNS.includes(key) ? "sticky" : "static",
                              left: IDENTITY_COLUMNS.includes(key) ? `${STICKY_LEFT_MAP[key]}px` : undefined,
                              zIndex: IDENTITY_COLUMNS.includes(key) ? 3 : 1,
                              minWidth:
                                key === "profile" ? 92 :
                                key === "name" ? 200 :
                                key === "uniqueId" ? 180 :
                                key === "email" ? 220 : undefined,
                            }}
                          >
                            {renderCell(user, key)}
                          </TableCell>
                        ))}

                        <TableCell
                          align="right"
                          sx={{
                            position: "sticky",
                            right: 0,
                            bgcolor: alpha(theme.palette.background.paper, 0.98),
                            zIndex: 3,
                            minWidth: 170,
                          }}
                        >
                          <Stack direction="row" spacing={0.6} justifyContent="flex-end">
                            {!user.isDeleted && userActions.canLoginAsUser && (
                              <Tooltip title="Login as User">
                                <IconButton
                                  size="small"
                                  onClick={() => handleLoginAsUser(user._id)}
                                  sx={actionButtonSx("warning")}
                                >
                                  <LogIn size={16} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {!user.isDeleted && canAssignDevice && (
                              <Tooltip title="Assign Device">
                                <IconButton
                                  size="small"
                                  onClick={() => openAssignDeviceModal(user)}
                                  sx={actionButtonSx("primary")}
                                >
                                  <FleetVehicleIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {!user.isDeleted && canAssignUserToGA && (
                              <Tooltip title="Link User To Group">
                                <IconButton
                                  size="small"
                                  onClick={() => openAssignGroupModal(user)}
                                  sx={actionButtonSx("success")}
                                >
                                  <FleetUserAssignIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {userActions.canEdit && (
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setEditingUser({
                                      ...user,
                                      isActive: user.isActive ?? true,
                                      devicePermissions: user.devicePermissions || {
                                        create: false,
                                        read: false,
                                        update: false,
                                        delete: false,
                                        command: false,
                                      },
                                      userPermissions: user.userPermissions || {
                                        create: false,
                                        read: false,
                                        update: false,
                                        delete: false,
                                        assign: false,
                                      },
                                    })
                                  }
                                  sx={actionButtonSx("primary")}
                                >
                                  <EditIcon size={16} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {userActions.canResetPassword && (
                              <Tooltip title="Reset Password">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setResetUserId(user._id);
                                    setShowResetPasswordModal(true);
                                  }}
                                  sx={actionButtonSx("warning")}
                                >
                                  <KeyRound size={16} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {user.isDeleted ? (
                              userActions.canRestore && (
                                <Tooltip title="Restore">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRestoreUser(user._id)}
                                    sx={actionButtonSx("success")}
                                  >
                                    <RestoreIcon size={16} />
                                  </IconButton>
                                </Tooltip>
                              )
                            ) : (
                              userActions.canDelete && (
                                <Tooltip title="Delete">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteUser(user._id)}
                                      disabled={user._id === currentUserId && currentUserRole === ROLES.SUPER_ADMIN}
                                      sx={{
                                        ...actionButtonSx("error"),
                                        "&.Mui-disabled": {
                                          color: alpha(theme.palette.error.main, 0.45),
                                          backgroundColor: alpha(theme.palette.error.main, 0.05),
                                          borderColor: alpha(theme.palette.error.main, 0.08),
                                        },
                                      }}
                                    >
                                      <TrashIcon size={16} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </ScrollableTableContainer>

            <Divider />

            <TablePagination
              component="div"
              count={sortedUsers.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </>
        ) : (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 1000, fontSize: 18 }}>No users found</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.7 }}>
              Try adjusting your search or filters.
            </Typography>
          </Box>
        )}
      </Paper>

      <Dialog
        open={assignDeviceModalOpen}
        onClose={() => setAssignDeviceModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 1000 }}>Link Device to User</DialogTitle>
        <form
          onSubmit={(e) => {
            handleAssignDevice?.(e);
          }}
        >
          <DialogContent sx={{ pt: 1.5 }}>
            <Stack spacing={2}>
              <Autocomplete
                options={safeDevices}
                getOptionLabel={(device) => `${device?.name || "Device"} (ID: ${device?.deviceId || "-"})`}
                value={safeDevices.find((device) => String(device?._id) === String(assignDeviceId)) || null}
                onChange={(_, value) => {
                  setAssignDeviceId?.(value?._id || "");
                  setDeviceSearchQuery?.(value ? `${value.name || "Device"} (ID: ${value.deviceId || "-"})` : "");
                }}
                inputValue={deviceSearchQuery || ""}
                onInputChange={(_, value, reason) => {
                  if (reason === "input") {
                    setDeviceSearchQuery?.(value);
                    setAssignDeviceId?.("");
                  }
                }}
                renderInput={(params) => <TextField {...params} label="Search Device" required />}
              />

              <Autocomplete
                options={assignableUsersForDevices}
                getOptionLabel={(u) => `${u?.username || u?.name || "User"} (${u?.mobileNo || u?.email || "-"})`}
                value={assignableUsersForDevices.find((u) => String(u?._id) === String(assignToUserId)) || null}
                onChange={(_, value) => {
                  setAssignToUserId?.(value?._id || "");
                  setUserSearchQuery?.(
                    value ? `${value.username || value.name || "User"} (${value.mobileNo || value.email || "-"})` : ""
                  );
                }}
                inputValue={userSearchQuery || ""}
                onInputChange={(_, value, reason) => {
                  if (reason === "input") {
                    setUserSearchQuery?.(value);
                    setAssignToUserId?.("");
                  }
                }}
                renderInput={(params) => <TextField {...params} label="Assign To" required />}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={() => setAssignDeviceModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!assignDeviceId || !assignToUserId}>
              Assign Device
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={assignGroupModalOpen}
        onClose={() => setAssignGroupModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 1000 }}>Add User to Group</DialogTitle>
        <form
          onSubmit={(e) => {
            handleAssignUserToGroupAdmin?.(e);
          }}
        >
          <DialogContent sx={{ pt: 1.5 }}>
            <Stack spacing={2}>
              <Autocomplete
                options={assignableUsersForGroupAdmin}
                getOptionLabel={(u) => `${u?.username || u?.name || "User"}`}
                value={
                  assignableUsersForGroupAdmin.find(
                    (u) => String(u?._id) === String(assignUserToGroupAdminUserId)
                  ) || null
                }
                onChange={(_, value) => {
                  setAssignUserToGroupAdminUserId?.(value?._id || "");
                  setAssignUserSearchQuery?.(value?.username || value?.name || "");
                }}
                inputValue={assignUserSearchQuery || ""}
                onInputChange={(_, value, reason) => {
                  if (reason === "input") {
                    setAssignUserSearchQuery?.(value);
                    setAssignUserToGroupAdminUserId?.("");
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label={assignmentLabels?.userLabel || "Select User"} required />
                )}
              />

              <Autocomplete
                options={groupAdmins}
                getOptionLabel={(u) => `${u?.username || u?.name || "Admin"}`}
                value={groupAdmins.find((u) => String(u?._id) === String(assignUserToGroupAdminTargetId)) || null}
                onChange={(_, value) => {
                  setAssignUserToGroupAdminTargetId?.(value?._id || "");
                  setGroupAdminSearchQuery?.(value?.username || value?.name || "");
                }}
                inputValue={groupAdminSearchQuery || ""}
                onInputChange={(_, value, reason) => {
                  if (reason === "input") {
                    setGroupAdminSearchQuery?.(value);
                    setAssignUserToGroupAdminTargetId?.("");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={assignmentLabels?.adminLabel || "Select Admin/Dealer"}
                    placeholder={assignmentLabels?.adminPlaceholder || ""}
                    required
                  />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={() => setAssignGroupModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!assignUserToGroupAdminUserId || !assignUserToGroupAdminTargetId}
            >
              Link to Group
            </Button>
          </DialogActions>
        </form>
      </Dialog>

    </Box>
  );
}

/** =========================
 * EDIT USER MODAL (same features)
 * ========================= */
function EditUserModal({
  editingUser,
  setEditingUser,
  handleEditUser,
  currentUser,
  currentUserId,
  currentUserRole,
  creatableRoles,
}) {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const [showAdvanced, setShowAdvanced] = useState(false);

  if (!editingUser) return null;

  if (normalizeRole(editingUser.role) === ROLES.DEALER) {
    return (
      <AddDealerModal
        open={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        onSuccess={() => setEditingUser(null)}
        mode="edit"
        initialData={editingUser}
      />
    );
  }

  if (normalizeRole(editingUser.role) === ROLES.SUB_ADMIN) {
    return (
      <AddSubAdminModal
        open={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        onSuccess={() => setEditingUser(null)}
        mode="edit"
        initialData={editingUser}
      />
    );
  }

  if (normalizeRole(editingUser.role) === ROLES.USER) {
    return (
      <AddUserModal
        open={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        onSuccess={() => setEditingUser(null)}
        mode="edit"
        initialData={editingUser}
      />
    );
  }

  if (normalizeRole(editingUser.role) === ROLES.VIEWER) {
    return (
      <RoleBasedUserFormModal
        role={ROLES.VIEWER}
        open={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        currentUser={currentUser}
        currentUserRole={currentUserRole}
        initialData={editingUser}
      />
    );
  }

  if (
    [
      ROLES.MANAGER,
      ROLES.HR,
      ROLES.OFFICE_EMPLOYEE,
      ROLES.FIELD_EMPLOYEE,
    ].includes(normalizeRole(editingUser.role))
  ) {
    return (
      <CreateUserModal
        setShowCreateUserModal={(value) => {
          if (!value) setEditingUser(null);
        }}
        currentUserRole={currentUserRole}
        currentUser={currentUser}
        mode="edit"
        initialData={editingUser}
        onSuccess={() => setEditingUser(null)}
      />
    );
  }

  return (
    <Dialog
      open={Boolean(editingUser)}
      onClose={() => setEditingUser(null)}
      fullWidth
      maxWidth="md"
      fullScreen={isSmDown}
      PaperProps={{ sx: { borderRadius: isSmDown ? 0 : 4 } }}
    >
      <DialogTitle sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontWeight: 1000 }}>Edit User Details</Typography>
          <IconButton onClick={() => setEditingUser(null)}>
            <X size={20} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <form onSubmit={(e) => handleEditUser(e, editingUser._id)}>
        <DialogContent sx={{ p: 2.25 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="User Name"
                value={editingUser.username}
                onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Email Address"
                type="email"
                value={editingUser.email}
                onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Mobile Number"
                value={editingUser.mobileNo || ""}
                onChange={(e) => setEditingUser({ ...editingUser, mobileNo: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", mb: 0.6 }}>
                  Role
                </Typography>
                <Select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                  disabled={editingUser._id === currentUserId && currentUserRole === ROLES.SUPER_ADMIN}
                >
                  {creatableRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {roleLabel(role)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Button
            type="button"
            variant="text"
            onClick={() => setShowAdvanced((p) => !p)}
            sx={{ fontWeight: 1000, textTransform: "none" }}
          >
            {showAdvanced ? "Hide Advanced Settings" : "Show Advanced Settings"}
          </Button>

          {showAdvanced && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.8, borderRadius: 3 }}>
                    <Typography sx={{ fontWeight: 1000, mb: 1 }}>Account Status</Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={editingUser.isActive ? "active" : "disabled"}
                        onChange={(e) =>
                          setEditingUser({ ...editingUser, isActive: e.target.value === "active" })
                        }
                        disabled={editingUser._id === currentUser?._id && currentUser?.role === ROLES.SUPER_ADMIN}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="disabled">Disabled</MenuItem>
                      </Select>
                    </FormControl>
                  </Paper>
                </Grid>

                {/* ✅ Permissions UI still in edit modal */}
                <Grid item xs={12} md={8}>
                  <Paper variant="outlined" sx={{ p: 1.8, borderRadius: 3 }}>
                    <Typography sx={{ fontWeight: 1000, mb: 1 }}>Device Permissions</Typography>
                    <FormGroup row>
                      {["create", "read", "update", "delete", "command"].map((perm) => (
                        <FormControlLabel
                          key={perm}
                          control={
                            <Checkbox
                              checked={Boolean(editingUser.devicePermissions?.[perm])}
                              onChange={(e) =>
                                setEditingUser({
                                  ...editingUser,
                                  devicePermissions: {
                                    ...editingUser.devicePermissions,
                                    [perm]: e.target.checked,
                                  },
                                })
                              }
                            />
                          }
                          label={perm.charAt(0).toUpperCase() + perm.slice(1)}
                        />
                      ))}
                    </FormGroup>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 1.8, borderRadius: 3, mt: 2 }}>
                    <Typography sx={{ fontWeight: 1000, mb: 1 }}>User Management Permissions</Typography>
                    <FormGroup row>
                      {["create", "read", "update", "delete", "assign"].map((perm) => (
                        <FormControlLabel
                          key={perm}
                          control={
                            <Checkbox
                              checked={Boolean(editingUser.userPermissions?.[perm])}
                              onChange={(e) =>
                                setEditingUser({
                                  ...editingUser,
                                  userPermissions: {
                                    ...editingUser.userPermissions,
                                    [perm]: e.target.checked,
                                  },
                                })
                              }
                            />
                          }
                          label={perm.charAt(0).toUpperCase() + perm.slice(1)}
                        />
                      ))}
                    </FormGroup>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}>
          <Button variant="outlined" onClick={() => setEditingUser(null)} sx={{ borderRadius: 2, fontWeight: 900 }}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" sx={{ borderRadius: 2, fontWeight: 900 }}>
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default UserManagement;
function RoleBasedUserFormModal({
  role,
  open,
  onClose,
  currentUser,
  currentUserRole,
  initialData = null,
}) {
  if (!open || !role) return null;

  const normalized = normalizeRole(role);

  if (normalized === ROLES.DEALER) {
    return <AddDealerModal open={open} onClose={onClose} onSuccess={onClose} mode="create" />;
  }

  if (normalized === ROLES.SUB_ADMIN) {
    return <AddSubAdminModal open={open} onClose={onClose} onSuccess={onClose} mode="create" />;
  }

  if (normalized === ROLES.USER) {
    return (
      <AddUserModal
        open={open}
        onClose={onClose}
        onSuccess={onClose}
        mode={initialData ? "edit" : "create"}
        initialData={initialData}
      />
    );
  }

  if (normalized === ROLES.VIEWER) {
    return (
      <ViewerPortalModal
        open={open}
        onClose={onClose}
        entityType="viewer"
        mode={initialData ? "edit" : "create"}
        initialData={initialData}
      />
    );
  }

  if ([ROLES.MANAGER, ROLES.HR, ROLES.OFFICE_EMPLOYEE, ROLES.FIELD_EMPLOYEE].includes(normalized)) {
    return (
      <CreateUserModal
        setShowCreateUserModal={(value) => {
          if (!value) onClose();
        }}
        currentUserRole={currentUserRole}
        currentUser={currentUser}
        mode="create"
        initialData={{ role: normalized }}
        onSuccess={onClose}
      />
    );
  }

  return null;
}

function CreateUserHubModal({
  open,
  onClose,
  users = [],
  creatableRoles = [],
  currentUser,
  currentUserRole,
  handleDeleteUser,
  handleRestoreUser,
  onDownloadUsersTemplate,
  onUsersFileSelected,
  setResetUserId,
  setShowResetPasswordModal,
  onRefresh,
  canLoginAsUser = true,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [impersonateUser] = useImpersonateUserMutation();
  const uploadRef = React.useRef(null);
  const [roleTab, setRoleTab] = useState("");
  const [searchName, setSearchName] = useState("");
  const [searchLoginId, setSearchLoginId] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [createRole, setCreateRole] = useState("");
  const [editingHubUser, setEditingHubUser] = useState(null);

  const roleTabs = useMemo(() => {
    const normalizedRoles = Array.from(
      new Set((creatableRoles || []).map((role) => normalizeRole(role)).filter(Boolean))
    );
    const preferredOrder = [
      ROLES.MANAGER,
      ROLES.HR,
      ROLES.OFFICE_EMPLOYEE,
      ROLES.FIELD_EMPLOYEE,
      ROLES.SUB_ADMIN,
      ROLES.DEALER,
      ROLES.CLIENT,
      ROLES.USER,
      ROLES.VIEWER,
    ];
    return normalizedRoles
      .filter((role) => preferredOrder.includes(role))
      .sort((a, b) => {
      const aIndex = preferredOrder.indexOf(a);
      const bIndex = preferredOrder.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return roleLabel(a).localeCompare(roleLabel(b));
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
      });
  }, [creatableRoles]);

  useEffect(() => {
    if (!open) return;
    setRoleTab((prev) => prev || roleTabs[0] || "");
    setSearchName("");
    setSearchLoginId("");
    setSearchPhone("");
    setPage(0);
  }, [open, roleTabs]);

  const filteredUsers = useMemo(() => {
    const nameQuery = searchName.trim().toLowerCase();
    const loginQuery = searchLoginId.trim().toLowerCase();
    const phoneQuery = searchPhone.trim();
    return (Array.isArray(users) ? users : [])
      .filter((user) => !roleTab || normalizeRole(user?.role) === roleTab)
      .filter((user) => {
        const name = String(user?.username || "").toLowerCase();
        const login = String(user?.uniqueId || "").toLowerCase();
        const phone = String(user?.mobileNo || "");
        if (nameQuery && !name.includes(nameQuery)) return false;
        if (loginQuery && !login.includes(loginQuery)) return false;
        if (phoneQuery && !phone.includes(phoneQuery)) return false;
        return true;
      });
  }, [users, roleTab, searchName, searchLoginId, searchPhone]);

  const roleCounts = useMemo(() => {
    return roleTabs.reduce((acc, role) => {
      acc[role] = (Array.isArray(users) ? users : []).filter((user) => normalizeRole(user?.role) === role).length;
      return acc;
    }, {});
  }, [roleTabs, users]);

  const pagedUsers = useMemo(
    () => filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUsers, page, rowsPerPage]
  );

  const openCreateForCurrentRole = () => {
    if (!roleTab) return;
    setCreateRole(roleTab);
  };

  const handleHubLoginAsUser = async (targetUserId) => {
    if (!canLoginAsUser) {
      alert("You don't have permission to login as other users.");
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to log in as this user? You will be logged out of Admin."
      )
    ) {
      return;
    }

    try {
      const currentAdminToken = localStorage.getItem("token");
      if (currentAdminToken) localStorage.setItem("adminFallbackToken", currentAdminToken);

      const response = await impersonateUser(targetUserId).unwrap();
      dispatch(adminApi.util.resetApiState());
      dispatch(loginSuccess({ user: response.user, token: response.token }));
      navigate("/dashboard", { replace: true, state: { __impersonate: true, t: Date.now() } });
    } catch (error) {
      alert(error?.data?.msg || "Failed to login as user.");
    }
  };

  const handleEditFromHub = (user) => {
    setEditingHubUser(user);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <DialogTitle sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontWeight: 1000, fontSize: 22 }}>User Control Center</Typography>
            <IconButton onClick={onClose}>
              <X size={20} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: "grey.50" }}>
          <Box sx={{ p: 2.5 }}>
            <Stack spacing={2}>
              <Tabs
                value={roleTab}
                onChange={(_, value) => {
                  setRoleTab(value);
                  setPage(0);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 42,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 900,
                    minHeight: 42,
                    px: 2,
                    borderRadius: 2,
                  },
                  "& .MuiTabs-flexContainer": { gap: 1 },
                }}
              >
                {roleTabs.map((role) => {
                  const meta = CREATE_ROLE_CATALOG[role];
                  const Icon = meta?.icon;
                  return (
                    <Tab
                      key={role}
                      value={role}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          {Icon ? <Icon sx={{ fontSize: 16 }} /> : null}
                          <Typography component="span" sx={{ fontWeight: 900 }}>
                            {roleLabel(role)}
                          </Typography>
                          <Chip
                            size="small"
                            label={roleCounts[role] ?? 0}
                            sx={{ height: 22, fontWeight: 900, borderRadius: 1.5 }}
                          />
                        </Stack>
                      }
                      sx={{
                        bgcolor: roleTab === role ? alpha(theme.palette.primary.main, 0.12) : "transparent",
                        border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                      }}
                    />
                  );
                })}
              </Tabs>

              <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} alignItems={{ xs: "stretch", lg: "center" }}>
                <TextField
                  placeholder="Search by name"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search size={16} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  placeholder="Search by Login Id"
                  value={searchLoginId}
                  onChange={(e) => setSearchLoginId(e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search size={16} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  placeholder="Search by phone"
                  value={searchPhone}
                  onChange={(e) => setSearchPhone(e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search size={16} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Stack direction="row" spacing={1}>
                  <Tooltip title="Add New">
                    <IconButton onClick={openCreateForCurrentRole} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.7)}`, borderRadius: 2 }}>
                      <Plus size={18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Import Users">
                    <IconButton onClick={() => uploadRef.current?.click()} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.7)}`, borderRadius: 2 }}>
                      <Upload size={18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export Template">
                    <IconButton onClick={onDownloadUsersTemplate} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.7)}`, borderRadius: 2 }}>
                      <Download size={18} />
                    </IconButton>
                  </Tooltip>
                  <input ref={uploadRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={onUsersFileSelected} />
                </Stack>
              </Stack>

              <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
                <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`, bgcolor: alpha(theme.palette.common.black, 0.03) }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <FormControl size="small" sx={{ minWidth: 90 }}>
                      <Select
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(Number(e.target.value));
                          setPage(0);
                        }}
                      >
                        {[10, 20, 50].map((size) => (
                          <MenuItem key={size} value={size}>
                            {size}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Typography variant="body2" color="text.secondary">
                      {filteredUsers.length} records
                    </Typography>
                  </Stack>
                </Box>

                <ScrollableTableContainer sx={{ maxHeight: "60vh" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 1000 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 1000 }}>User ID</TableCell>
                        <TableCell sx={{ fontWeight: 1000 }}>Mobile</TableCell>
                        <TableCell sx={{ fontWeight: 1000 }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 1000 }}>Remaining Tokens</TableCell>
                        <TableCell sx={{ fontWeight: 1000 }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 1000 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedUsers.map((user) => (
                        <TableRow key={unwrapMongoValue(user._id) || user._id} hover>
                          <TableCell>{user.username || "-"}</TableCell>
                          <TableCell>{user.uniqueId || "-"}</TableCell>
                          <TableCell>{user.mobileNo || "-"}</TableCell>
                          <TableCell>{roleLabel(user.role)}</TableCell>
                          <TableCell>
                            <CreditsCell
                              credits={user.credits}
                              licenseSummary={user.licenseSummary}
                              remainingTokens={user.remainingTokens}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={user.isDeleted ? "Deleted" : user.isActive === false ? "Disabled" : "Active"}
                              color={user.isDeleted ? "error" : user.isActive === false ? "warning" : "success"}
                              sx={{ fontWeight: 800, borderRadius: 2 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.6} justifyContent="flex-end">
                              {!user.isDeleted && canLoginAsUser && (
                                <Tooltip title="Login as User">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleHubLoginAsUser(user._id)}
                                    sx={{ bgcolor: "#f8fafc", borderRadius: 1 }}
                                  >
                                    <LogIn size={16} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => handleEditFromHub(user)}>
                                  <EditIcon size={16} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Permissions">
                                <IconButton size="small" onClick={() => handleEditFromHub(user)}>
                                  <PermissionIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reset Password">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setResetUserId?.(user._id);
                                    setShowResetPasswordModal?.(true);
                                  }}
                                >
                                  <KeyRound size={16} />
                                </IconButton>
                              </Tooltip>
                              {user.isDeleted ? (
                                <Tooltip title="Restore">
                                  <IconButton size="small" onClick={() => handleRestoreUser?.(user._id)}>
                                    <RestoreIcon size={16} />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Delete">
                                  <IconButton size="small" onClick={() => handleDeleteUser?.(user._id)}>
                                    <TrashIcon size={16} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollableTableContainer>

                <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Showing {filteredUsers.length ? page * rowsPerPage + 1 : 0} to {Math.min((page + 1) * rowsPerPage, filteredUsers.length)} of {filteredUsers.length} entries
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                        Prev
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={(page + 1) * rowsPerPage >= filteredUsers.length}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>

      <RoleBasedUserFormModal
        role={createRole}
        open={Boolean(createRole)}
        onClose={() => {
          setCreateRole("");
          onRefresh?.();
        }}
        currentUser={currentUser}
        currentUserRole={currentUserRole}
      />

      <EditUserModal
        editingUser={editingHubUser}
        setEditingUser={setEditingHubUser}
        handleEditUser={() => {}}
        currentUser={currentUser}
        currentUserId={currentUser?._id || currentUser?.id}
        currentUserRole={currentUserRole}
        creatableRoles={creatableRoles}
      />
    </>
  );
}

export { EditUserModal, CreateUserHubModal };
